import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Ingrese su correo y contraseña');
      return;
    }

    try {
      setLoading(true);
      const result = await login(email, password);
      if (!result.ok) {
        setError(result.error || 'Correo o contraseña incorrectos');
      }
    } catch (err: any) {
      setError(err.message || 'Error de conexión');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4 py-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary-100/60 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-accent-100/60 blur-3xl pointer-events-none"></div>

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-2xl border border-background-200 p-8 shadow-sm">
          <div className="flex flex-col items-center mb-8 text-center">
            <div className="w-14 h-14 rounded-xl bg-primary-500 flex items-center justify-center mb-4 text-white">
              <i className="ri-radar-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-900">CRM Wialon</h1>
            <p className="text-sm text-foreground-500 mt-1">Inicie sesión para acceder a su panel</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 mb-4">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Correo electrónico</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@empresa.com"
                  autoComplete="username"
                  required
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 placeholder:text-foreground-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Contraseña</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  required
                  className="w-full pl-9 pr-10 py-2.5 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-60 cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Ingresando...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line"></i>
                  Iniciar Sesión
                </>
              )}
            </button>
          </form>

          <p className="text-[11px] text-foreground-400 text-center mt-6">
            Acceso restringido al personal autorizado de la plataforma
          </p>
        </div>
      </div>
    </div>
  );
}
